import {capitalize, compact, filter, get, includes, isNil, map, omitBy, some} from 'lodash';
import {MONTH_ABBRVS, MONTH_FULL, consolidateSchoolYears} from 'shared/utils';
import {EarningStatus} from 'src/types/enums';
import {formattedDate} from '../utils';

// student-profile
import {formatEarnings} from 'src/utils/formatters';
import {TEST_SECTION_NAMES, testScoreName, testScoreText} from '../utils/tests';

export default class StudentData {
  constructor({student, collegeDataForStudent, hideSensitiveDemographics}) {
    this.student = student;
    this.portfolio = student.portfolio;
    this.collegeDataForStudent = collegeDataForStudent || {};
    this.hideSensitiveDemographics = hideSensitiveDemographics;
    this.info = {};
  }

  hasPortfolioItems = () =>
    some([
      this.hasCourses(),
      this.hasActivities(),
      this.hasSports(),
      this.hasTestScores(),
      this.hasJobsAndVolunteering(),
      this.hasHonorsAndAwards(),
      this.hasCollegeSuccessItems(),
      this.hasMultimediaItems(),
    ]);

  gpa = () => {
    if (!this.info.gpa) {
      return null;
    }

    const gpa = (Math.round(this.info.gpa * 100) / 100).toFixed(2);

    return gpa.substr(3, 1) === '0' ? gpa.substr(0, 3) : gpa;
  };

  totalCredits = () => this.student.totalCredits;

  hasGpa = () => !!this.gpa();

  gpaExplanationText = () => null;

  isFrl = () => (this.hideSensitiveDemographics ? null : false);

  isMilitary = () => false;

  isLowIncome = () => (this.hideSensitiveDemographics ? null : false);

  hasCourses = () => false;

  courses = () => [];

  isFirstGen = () => (this.hideSensitiveDemographics ? null : !!this.student.isFirstGen);

  studentName = () => `${this.student.firstName} ${this.student.lastName}`;

  awardsCount = () =>
    this.collegeDataForStudent.partnerScholarships.reduce(
      (count, {awards}) => count + awards.length,
      0
    );

  institutionName = () => this.student.currentInstitution?.name;

  institutionLocation = () =>
    `${this.student.currentInstitution?.city}, ${this.student.currentInstitution?.subdivision}`;

  submissionDate = () => {
    if (
      !this.collegeDataForStudent.submissionStatus ||
      !this.collegeDataForStudent.submissionStatus.submittedAt
    ) {
      return null;
    }

    return new Date(this.collegeDataForStudent.submissionStatus.submittedAt);
  };

  hasEarnings = () => this.earningsFormatter().earningsPresent;

  earningsFormatter = () =>
    formatEarnings(this.collegeDataForStudent.amount, this.collegeDataForStudent.divisor || 4);

  hasEarningStatusType = () => {
    const statusType = get(this.collegeDataForStudent, 'earningRelationship.statusType');
    return includes(EarningStatus, statusType);
  };

  isSubmitted = () => !!this.submissionDate();

  isFollowing = () => this.collegeDataForStudent.isFollowing;

  isInState = () => this.collegeDataForStudent && !!this.collegeDataForStudent.userInState;

  applicationStatus = () =>
    this.collegeDataForStudent &&
    this.collegeDataForStudent.applicationStatus &&
    this.collegeDataForStudent.applicationStatus.status;

  formatActivity = (activity) => {
    const place = activity.institution ? activity.institution.name : activity.organization;
    const commitment = `${capitalize(activity.participation)} Commitment`;

    return {
      name: activity.name,
      subtitle: `${consolidateSchoolYears(activity.schoolYears).join(
        ', '
      )} • ${commitment} • ${place}`,
      description: activity.description,
      subItems: map(activity.leadershipPositions, (subItem) => ({
        title: `${subItem.schoolYear} • ${subItem.title}`,
        description: subItem.description,
      })),
    };
  };

  activities = () =>
    map(filter(this.portfolio.activities, {activityType: 'activity'}), this.formatActivity);

  hasActivities = () => !!this.activities().length;

  sports = () =>
    map(filter(this.portfolio.activities, {activityType: 'sport'}), this.formatActivity);

  hasSports = () => !!this.sports().length;

  formatTestScore = (testScore) => ({
    name: testScoreName(testScore),
    subtitle: `${testScore.year} • ${MONTH_FULL[testScore.month]} • ${testScoreText(testScore)}`,
    description: this.formatTestSectionScores(testScore),
  });

  formatTestSectionScores = (testScore) => {
    // ap scores don't have sections & we don't format 'other' test sections
    if (testScore.type === 'ap' || testScore.type === 'other') {
      return;
    }

    let sections = testScore.scores;
    // SAT score allows null for 'essay' and 'multiple choice' sections
    sections = omitBy(sections, isNil);

    return map(sections, (score, section) => `${TEST_SECTION_NAMES[section]} - ${score}`).join(
      ' | '
    );
  };

  testScores = () => map(this.portfolio.testScores, this.formatTestScore);

  hasTestScores = () => !!this.portfolio.testScores.length;

  formatWorkExperience = (workExperience) => {
    const subtitleParts = [
      this.jobAndVolunteeringDateRange(workExperience, workExperience.currentWorkplace),
      `${workExperience.hoursPerWeek} hrs / wk`,
    ];

    if (workExperience.paid) {
      subtitleParts.push('Paid');
    }

    if (workExperience.familyService) {
      subtitleParts.push('Support Family');
    }

    return {
      name: `${workExperience.place}${workExperience.role ? ` - ${workExperience.role}` : ''}`,
      subtitle: subtitleParts.join(' • '),
      description: workExperience.responsibilities,
      date: new Date(workExperience.startYear, workExperience.startMonth),
    };
  };

  jobAndVolunteeringDateRange = ({startYear, startMonth, endYear, endMonth}, present = false) => {
    let range = `${MONTH_ABBRVS[startMonth]} ${startYear}`;

    if (present) {
      range = range + ' - Present';
    } else if (endYear && (endMonth || endMonth === 0)) {
      range = range + ` - ${MONTH_ABBRVS[endMonth]} ${endYear}`;
    }

    return range;
  };

  workExperiences = () => map(this.portfolio.workExperiences, this.formatWorkExperience);

  formatCommunityServiceEvent = (communityServiceEvent) => {
    let subtitle = `${this.jobAndVolunteeringDateRange(communityServiceEvent)} • ${
      communityServiceEvent.hours
    } Hours`;

    if (communityServiceEvent.requiredByHs) {
      subtitle = subtitle + ' • Required';
    }

    return {
      name: `${communityServiceEvent.place}${
        communityServiceEvent.role ? ` - ${communityServiceEvent.role}` : ''
      }`,
      subtitle,
      description: communityServiceEvent.description,
      date: new Date(communityServiceEvent.startYear, communityServiceEvent.startMonth),
    };
  };

  communityServiceEvents = () =>
    map(this.portfolio.communityServiceEvents, this.formatCommunityServiceEvent);

  hasJobsAndVolunteering = () => !!this.jobsAndVolunteering().length;

  jobsAndVolunteering = () =>
    this.workExperiences()
      .concat(this.communityServiceEvents())
      .sort((a, b) => a.date > b.date);

  hasHonorsAndAwards = () => !!this.portfolio.awards.length;

  formatAward = ({issuer, name, year, description}) => ({
    name: name,
    subtitle: `${year} • ${issuer}`,
    description,
  });

  honorsAndAwards = () => map(this.portfolio.awards, this.formatAward);

  earningRelationship = () => this.collegeDataForStudent.earningRelationship;

  hasPerfectAttendance = () => false;

  /** @returns {{name: string, subtitle: string} | null} */
  attendance = () => null;

  hasFafsaSubmission = () => false;

  /** @returns {{name: string, subtitle: string} | null} */
  fafsaSubmission = () => null;

  hasCollegeSuccessItems = () => !!this.collegeSuccessItems().length;

  collegeEvents = () => {
    const events = this.portfolio.collegeEvents;
    const eventsToDisplay = this.collegeDataForStudent._id
      ? filter(events, (collegeEvent) => collegeEvent.collegeId === this.collegeDataForStudent._id)
      : events;

    return eventsToDisplay.map(({name, month, year, collegeName}) => ({
      name,
      subtitle: `${MONTH_ABBRVS[month]} ${year} • ${collegeName}`,
    }));
  };

  collegeSuccessItems = () => {
    const items = [];

    const attendance = this.attendance();
    if (this.hasPerfectAttendance() && attendance) {
      items.push(attendance);
    }

    const fafsaSubmission = this.hasFafsaSubmission() ? this.fafsaSubmission() : null;
    if (fafsaSubmission) {
      items.push(fafsaSubmission);
    }

    return items.concat(this.collegeEvents());
  };

  hasMultimediaItems = () => !!this.portfolio.multimediaItems.length;

  formatMultimedia = (multimedia) => ({
    name: multimedia.title,
    subtitle: `${MONTH_ABBRVS[multimedia.month]} ${multimedia.year} • ${multimedia.category}`,
  });

  multimediaItems = () => this.portfolio.multimediaItems;

  studentName = () => `${this.student.firstName} ${this.student.lastName}`;

  address = () => {
    if (this.hideSensitiveDemographics) {
      return null;
    }

    const {addressLine1, addressLine2, city, subdivision, postalCode} = this.student;
    return compact([addressLine1, addressLine2, city, subdivision, postalCode]).join(', ');
  };

  hasMobile = () => !!this.student.mobile;

  hasAcademicInterests = () => !!this.student.academicInterests.length;

  birthday = () =>
    formattedDate(new Date(this.student.birthday).toLocaleDateString(undefined, {timeZone: 'UTC'}));

  lastActive = () => formattedDate(this.student.lastActive);

  createdAt = () => formattedDate(this.student.createdAt * 1000);

  sex = () => {
    switch (this.student.sex) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      default:
        return 'Prefers not to answer';
    }
  };

  mobile = () => {
    if (this.hideSensitiveDemographics || !this.student.mobile) {
      return null;
    }

    const groups = this.student.mobile.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

    if (!groups[2]) {
      return groups[1];
    }

    return `(${groups[1]}) ${groups[2]}${groups[3] ? '-' + groups[3] : ''}`;
  };

  gender = () => {
    switch (this.student.gender) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'NB':
        return `Non-Binary: ${this.student.customGenderIdentity}`;
      default:
        return 'Prefers not to answer';
    }
  };

  // singular in name but actually an array
  ethnicity = () => {
    const selections = this.student.ethnicity;
    return selections && selections.length > 1 ? selections.join(', ') : selections;
  };

  additionalNotes = () => {
    const notes = [];
    this.isFrl() && notes.push('Free Reduced Lunch Program');
    this.isFirstGen() && notes.push('First Generation');
    this.isLowIncome() && notes.push('Low Income');

    return notes;
  };

  cannotMessageReason = () => this.student.cannotMessageReason;

  isSubscribedToEmailMessaging = () =>
    this.student.notificationSettings?.mentionsAndConnectionRequests?.email;
}
